import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  // The Sidekick side panel sits at `zIndex.modal` in overlay mode, so
  // raise the preview one tier to guarantee it covers the panel + scrim.
  root: {
    zIndex: theme.zIndex.modal + 1,
  },
  backdrop: {
    backgroundColor: theme.new.color.background.scrim,
  },
  paper: {
    // Figma "Activate all" modal (16844:42084) is a landscape 1291×793 box
    // (ratio ≈ 1.63). Sizing by width + aspect-ratio keeps the chart filling
    // the frame instead of letterboxing inside a 90vh-tall dialog.
    width: 'min(90vw, 1291px)',
    maxWidth: 1291,
    aspectRatio: '1291 / 793',
    maxHeight: '90vh',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: theme.new.shape.borderRadius.medium,
    backgroundColor: theme.new.color.common.white,
    // Mobile-web (Figma 17109:23462): full-bleed, no rounded corners.
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
      height: '100%',
      maxWidth: 'none',
      maxHeight: 'none',
      aspectRatio: 'auto',
      borderRadius: 0,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.new.spacing.medium,
    // Figma Modal/Web/Header (16844:42085): the header hugs its content with
    // top/right/left padding only (bottom 0) and carries NO bottom divider.
    paddingTop: theme.new.spacing.medium,
    paddingRight: theme.new.spacing.medium,
    paddingLeft: theme.new.spacing.medium,
    paddingBottom: 0,
    backgroundColor: theme.new.color.common.white,
  },
  title: {
    ...theme.typography.header2_medium,
    color: theme.new.color.textPrimary,
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconButton: {
    width: 40,
    height: 40,
    color: theme.new.color.textPrimary,
    padding: 0,
    transition: theme.transitions.create(['background-color', 'color']),
    '&:hover': {
      backgroundColor: theme.new.color.brand[100],
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.new.color.brand[600]}`,
      outlineOffset: '2px',
    },
  },
  // Zoom controls match the Figma footer button (15510:21157): a 32×32
  // ROUNDED-SQUARE (radius 8) — not a circle — around the 24px glyph.
  zoomButton: {
    width: 32,
    height: 32,
    padding: 0,
    color: theme.new.color.textPrimary,
    borderRadius: theme.new.shape.borderRadius.small,
    transition: theme.transitions.create(['background-color', 'color']),
    '&:hover': {
      backgroundColor: theme.new.color.brand[100],
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.new.color.brand[600]}`,
      outlineOffset: '2px',
    },
  },
  // The active zoom control reflects ZOOM STATE, not focus: Figma "Zoom In"
  // (16844:38360) fills the engaged control Brand 100 with a Brand 600 glyph
  // while the image is magnified. At fit (100%) neither control is active and
  // zoom-out is disabled. The hover override keeps the fill steady so the
  // active control doesn't flatten on pointer-over.
  zoomButtonActive: {
    backgroundColor: theme.new.color.brand[100],
    color: theme.new.color.brand[600],
    '&:hover': {
      backgroundColor: theme.new.color.brand[100],
    },
  },
  body: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    overflow: 'hidden',
    backgroundColor: theme.new.color.common.white,
  },
  imageContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    // Prevent the browser from hijacking pointer drags for scrolling.
    touchAction: 'none',
  },
  imageContainerZoomed: {
    cursor: 'grab',
  },
  imageContainerDragging: {
    cursor: 'grabbing',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    transformOrigin: 'center center',
    userSelect: 'none',
    willChange: 'transform',
  },
  imageAnimated: {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  controls: {
    // Figma footer (Modal/Web): bare zoom glyphs anchored bottom-RIGHT with a
    // ~16px inset — no wrapping pill, border, or background.
    position: 'absolute',
    bottom: theme.new.spacing.medium,
    right: theme.new.spacing.medium,
    display: 'flex',
    alignItems: 'center',
    gap: theme.new.spacing.small,
  },
}));

export default useStyles;
